import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Check, X } from 'lucide-react';
import { HoldToSeal } from './HoldToSeal';
import { SovereignFlame, Portrait } from './SovereignFlame';
import { REALMS, PILLARS, lastNDays } from './oathTheme';

const P = PILLARS[0];
const realmOf = (id) => REALMS.find((r) => r.id === id);

const StreakFlame = ({ streak, best }) => (
  <div className="flex items-center gap-4 rounded-2xl px-4 py-3" data-testid="oath-streak-card"
    style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.14), rgba(139,63,191,0.12))', border: `1px solid ${P.accent}40` }}>
    <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.8, repeat: Infinity }} className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ background: `radial-gradient(circle, ${streak ? 'rgba(255,140,50,0.55)' : 'rgba(255,255,255,0.06)'}, transparent 70%)` }}>
      <div style={{ opacity: streak ? 1 : 0.3, filter: streak ? 'none' : 'grayscale(1)' }}><SovereignFlame size={34 + Math.min(streak, 10)} /></div>
    </motion.div>
    <div className="flex-1">
      <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/45">Promises kept in a row</p>
      <p className="font-['Outfit'] text-4xl font-light leading-none mt-1" style={{ color: P.light }} data-testid="oath-streak-value">
        {streak}<span className="text-sm text-white/50 ml-2">day{streak === 1 ? '' : 's'}</span>
      </p>
    </div>
    <div className="text-right">
      <Crown size={14} className="ml-auto mb-1" style={{ color: P.accent }} />
      <p className="font-['Outfit'] text-[10px] text-white/40 uppercase tracking-[0.15em]">Best</p>
      <p className="font-['Outfit'] text-lg font-semibold" style={{ color: P.accent }} data-testid="oath-best-streak">{best}</p>
    </div>
  </div>
);

const Milestones = ({ list }) => (
  <div className="flex justify-between" data-testid="oath-milestones">
    {list.map((m) => (
      <div key={m.days} className="flex flex-col items-center gap-1">
        <div className="w-11 h-11 rounded-full flex items-center justify-center font-['Outfit'] text-sm font-semibold"
          style={m.earned ? { background: 'radial-gradient(circle at 40% 30%, #FFE9A0, #B8860B)', color: '#2a1a00', boxShadow: '0 0 14px rgba(212,175,55,0.7)' } : { border: '1px dashed rgba(212,175,55,0.3)', color: 'rgba(255,255,255,0.3)' }}>
          {m.days}
        </div>
        <span className="font-['Outfit'] text-[8px] uppercase tracking-[0.15em] text-white/35">days</span>
      </div>
    ))}
  </div>
);

const CheckIn = ({ pact, onKeep, busy, isToday }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl p-5 text-center" data-testid="oath-checkin"
    style={{ background: 'linear-gradient(160deg, rgba(212,175,55,0.16), rgba(11,8,20,0.9))', border: `1px solid ${P.accent}80`, boxShadow: `0 0 24px ${P.glow}` }}>
    <p className="font-['Outfit'] text-[10px] tracking-[0.3em] uppercase" style={{ color: P.accent }}>{isToday ? "Tonight's judgement" : "Yesterday's judgement"}</p>
    <p className="font-['Cormorant_Garamond'] italic text-xl text-white mt-2">"{pact.text}"</p>
    <p className="font-['Cinzel'] text-base mt-3" style={{ color: P.light }}>Did you keep your word?</p>
    <div className="grid grid-cols-2 gap-3 mt-4">
      <button type="button" disabled={busy} onClick={() => onKeep(pact.date, true)} data-testid="oath-kept-yes"
        className="py-3 rounded-full flex items-center justify-center gap-2 font-['Cinzel'] text-xs tracking-[0.2em] text-black active:scale-95 transition-transform"
        style={{ background: 'linear-gradient(135deg, #FFE9A0, #D4AF37)', boxShadow: '0 0 18px rgba(212,175,55,0.5)' }}><Check size={16} /> I KEPT IT</button>
      <button type="button" disabled={busy} onClick={() => onKeep(pact.date, false)} data-testid="oath-kept-no"
        className="py-3 rounded-full flex items-center justify-center gap-2 font-['Cinzel'] text-xs tracking-[0.2em] text-white/60 active:scale-95 transition-transform"
        style={{ border: '1px solid rgba(255,100,120,0.4)' }}><X size={16} /> I BROKE IT</button>
    </div>
    <p className="font-['Outfit'] text-[10px] text-white/35 mt-3">Be honest. Only kept promises build the streak.</p>
  </motion.div>
);

const SealedCard = ({ pact, user }) => {
  const r = realmOf(pact.realm);
  const status = pact.kept === true ? 'Kept · Honoured' : pact.kept === false ? 'Broken · Rise again tomorrow' : 'Sealed · In progress';
  return (
    <motion.div initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative rounded-3xl px-6 py-7 text-center overflow-hidden" data-testid="oath-sealed-card"
      style={{ background: 'radial-gradient(circle at 50% 0%, rgba(212,175,55,0.22), rgba(11,8,20,0.9) 70%)', border: `1px solid ${P.accent}66` }}>
      <motion.div className="absolute inset-0 pointer-events-none" animate={{ x: ['-120%', '120%'] }} transition={{ duration: 3.5, repeat: Infinity, repeatDelay: 2 }}
        style={{ background: 'linear-gradient(100deg, transparent 40%, rgba(255,233,160,0.12) 50%, transparent 60%)' }} />
      <div className="mx-auto mb-3 flex justify-center"><SovereignFlame size={110}><Portrait user={user} /></SovereignFlame></div>
      <p className="font-['Outfit'] text-[10px] tracking-[0.3em] uppercase" style={{ color: pact.kept === false ? '#FF8C9A' : P.accent }} data-testid="oath-pact-status">{status}</p>
      <p className="font-['Cormorant_Garamond'] text-2xl italic text-white mt-3 leading-snug break-words [overflow-wrap:anywhere]" data-testid="oath-sealed-text">"{pact.text}"</p>
      <p className="font-['Outfit'] text-[11px] text-white/35 mt-4">Signed {new Date(pact.signed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {r?.label || 'Sovereign'} vow</p>
    </motion.div>
  );
};

const SignForm = ({ onSign, busy }) => {
  const [realm, setRealm] = useState('discipline');
  const [text, setText] = useState('');
  const r = realmOf(realm);
  return (
    <div className="space-y-4">
      <div>
        <p className="font-['Cinzel'] text-sm tracking-[0.15em]" style={{ color: P.light }}>Swear today's oath</p>
        <p className="font-['Outfit'] text-xs text-white/50 mt-1">Confidence is built one kept promise at a time. Pick a realm, choose your vow, and seal it like a king.</p>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {REALMS.map((x) => (
          <button key={x.id} type="button" onClick={() => { setRealm(x.id); setText(''); }} data-testid={`oath-realm-${x.id}`}
            className="rounded-2xl px-1 py-2 flex flex-col items-center gap-0.5 transition-transform active:scale-95"
            style={{ border: `1px solid ${realm === x.id ? x.color : 'rgba(255,255,255,0.08)'}`, background: realm === x.id ? `${x.color}1F` : 'rgba(255,255,255,0.02)', boxShadow: realm === x.id ? `0 0 14px ${x.color}55` : 'none' }}>
            <img src={`/oath/realm_${x.id}.png`} alt="" draggable={false} data-testid={`realm-icon-${x.id}`} style={{ width: 40, height: 40, objectFit: 'contain', filter: realm === x.id ? `drop-shadow(0 0 8px ${x.color})` : 'none' }} />
            <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.04em]" style={{ color: realm === x.id ? x.color : 'rgba(255,255,255,0.5)' }}>{x.label}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {r.vows.map((v, i) => (
          <button key={v} type="button" onClick={() => setText(v)} data-testid={`oath-preset-${i}`}
            className="w-full text-left rounded-xl px-4 py-2.5 font-['Cormorant_Garamond'] italic text-lg transition-colors duration-200"
            style={{ border: `1px solid ${text === v ? r.color : 'rgba(255,255,255,0.06)'}`, background: text === v ? `${r.color}18` : 'rgba(255,255,255,0.02)', color: text === v ? '#fff' : 'rgba(255,255,255,0.65)' }}>
            {v}
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 160))} rows={2} data-testid="oath-pact-input" placeholder="Or write your own: Today, I …"
        className="w-full rounded-2xl px-4 py-3 bg-white/[0.04] font-['Cormorant_Garamond'] text-xl italic text-white placeholder:text-white/25 outline-none resize-none"
        style={{ border: `1px solid ${r.color}50` }} />
      <div className="flex flex-col items-center gap-2 pt-1">
        <HoldToSeal accent={P.accent} disabled={busy || text.trim().length < 3} onSealed={() => onSign(text.trim(), realm)} label="Hold to swear" testId="oath-seal-btn" />
        <p className="font-['Outfit'] text-[10px] text-white/30 tracking-[0.15em] uppercase">Press &amp; hold · +10 power</p>
      </div>
    </div>
  );
};

const PactLedger = ({ pacts }) => {
  const by = Object.fromEntries(pacts.map((p) => [p.date, p]));
  return (
    <div className="flex justify-between gap-1" data-testid="oath-pact-ledger">
      {lastNDays(14).map((d) => {
        const p = by[d];
        const bg = !p ? 'rgba(255,255,255,0.06)' : p.kept === true ? `linear-gradient(180deg, ${P.light}, ${P.accent})` : p.kept === false ? 'rgba(255,100,120,0.35)' : 'transparent';
        return <div key={d} title={d} className="flex-1 h-6 rounded-sm" style={{ background: bg, border: p && p.kept == null ? `1px solid ${P.accent}` : 'none', boxShadow: p?.kept ? `0 0 8px ${P.glow}` : 'none' }} />;
      })}
    </div>
  );
};

export const OathPact = ({ state, onSign, onKeep, busy, user }) => {
  const pact = state.today_pact;
  const pending = state.pending_checkin;
  const eveningOrYesterday = pending && (pending.date !== state.today || new Date().getHours() >= 18);
  return (
    <div className="space-y-5">
      <StreakFlame streak={state.oath_streak} best={state.best_streak} />
      {eveningOrYesterday && <CheckIn pact={pending} onKeep={onKeep} busy={busy} isToday={pending.date === state.today} />}
      {pact ? <SealedCard pact={pact} user={user} /> : <SignForm onSign={onSign} busy={busy} />}
      {pact && pact.kept == null && !eveningOrYesterday && (
        <p className="font-['Outfit'] text-xs text-center text-white/45" data-testid="oath-evening-note">After 6 PM, return to confirm you kept your word and grow the flame.</p>
      )}
      <div>
        <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">Milestone crests</p>
        <Milestones list={state.milestones} />
      </div>
      <div>
        <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">Last 14 days · gold = kept</p>
        <PactLedger pacts={state.recent_pacts} />
      </div>
    </div>
  );
};

export default OathPact;
